import {
  AppendAssignedAreasOperationRequest,
  BookingsApi,
  Configuration,
  ReplaceAssignedAreasRequest,
  ViewBooking,
  ViewBookingAssignedAreasInner,
  ViewBookingCommentsInnerToJSON
} from "collins-js-sdk";
import { notification } from "../../utils/notifications";
import { BookingV4 } from "../../types/bookings";
import {
  type BookingUpdateContext,
  updateStateBooking
} from "../../utils/booking";
import {
  getBookingsListKey,
  updateBookingsList
} from "../../queries/bookingsListCache";
import { selectedBookingMerged } from "../../store/slices/diarySlice";
import {
  clearBookingWriteInFlight,
  markBookingWriteInFlight
} from "../../utils/bookingWriteTracker";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import {
  convertAllDatesToUTC,
  convertObjectCamelCaseToSnakeCase,
  convertObjectSnakeCaseToCamelCase,
  getDefinedProps
} from "../../utils/helpers";
import { updateBookingCacheStorage } from "../../utils/localStorage/apiCacheStorage";
import {
  assertPreorderTypes,
  v4PreorderToView
} from "../../utils/preorderMapper";
import { apiFetchNotOkHandler, errorHandler } from "../errorHandler";
import { getBuildVersion } from "../getDefaultHeaders";
import {
  isStaleBookingConflict,
  markStaleBookingConflict,
  readErrorMessage,
  STALE_BOOKING_NOTIFICATION
} from "./staleBookingConflict";

export { isStaleBookingConflict };

/**
 * Add or update the booking
 * @param booking
 */
export type editBooking = {
  unsaved?: boolean;
  deleted?: boolean;
  additionalAssignedAreas?: string[] | null;
};
type updateBookingType = {
  booking: ViewBooking & editBooking;
  diaryContext?: BookingUpdateContext;
};
/**
 * Serialises writes per booking id. A booking's optimistic concurrency is keyed
 * on last_updated: the API rejects any write whose last_updated is older than
 * the stored value ("Booking is out of date."). Two quick changes to the same
 * booking (e.g. Arrived then Left) would otherwise both build on the same
 * pre-change snapshot and fire in parallel — the second carries a now-stale
 * last_updated and is rejected, leaving the card stuck "Modified - Not Updated"
 * until a full refetch. Chaining writes per id lets each one rebase its
 * last_updated onto the previous write's server response before saving.
 */
const bookingWriteChains = new Map<
  string,
  Promise<ViewBooking & editBooking>
>();

// The freshest server last_updated we've seen for a booking, with when we
// recorded it. A rapid follow-up write (e.g. the early-checkout finish prompt
// firing right after the Left stage save) rebases its last_updated onto this
// exact server string, so the API can't judge it stale at its second-precision
// clock. Time-bounded: isolated writes (minutes later, or after an out-of-band
// change) fall back to `new Date()`, preserving the original behaviour.
const recentServerVersion = new Map<
  string,
  { lastUpdated: ViewBooking["lastUpdated"]; at: number }
>();
const RECENT_VERSION_TTL_MS = 60_000;

/** Records the server's current last_updated for a booking so the next write
 * rebases onto that exact string. Callers that refetch a booking immediately
 * before writing (e.g. confirming a just-created enquiry, whose record the
 * server updates about a second after creation) use this to avoid posting a
 * snapshot the API would reject as out of date. */
export const recordServerBookingVersion = (
  bookingId: string,
  lastUpdated: ViewBooking["lastUpdated"]
): void => {
  recentServerVersion.set(bookingId, { lastUpdated, at: Date.now() });
};

/** Drops the cached server version for a booking. Anything that changes a
 * booking outside addOrUpdateBooking (an enquiry Confirm or Reject, a V4 edit)
 * moves the server's last_updated past the cached string, and the response
 * never reaches this cache. Left in place, the next write inside the TTL
 * rebases onto the superseded value and the API rejects it as out of date,
 * leaving the card "Modified - Not Updated". Forgetting it makes that write
 * fall back to a fresh timestamp, as an isolated write does. */
export const forgetServerBookingVersion = (bookingId: string): void => {
  recentServerVersion.delete(bookingId);
};

const persistBooking = async (
  booking: ViewBooking & editBooking,
  diaryContext?: BookingUpdateContext
): Promise<ViewBooking & editBooking> => {
  const bookingResponse = await saveBooking({ ...booking });

  if (!diaryContext) {
    return bookingResponse;
  }
  if (bookingResponse) {
    updateStateBooking(bookingResponse, diaryContext);
  }

  // The selected booking's V4 document is loaded by TanStack Query and copied
  // into selectedBookingV4 whenever it resolves. Without invalidating it here,
  // a GET in flight when this write commits (or a cached pre-edit snapshot)
  // would overwrite the panel with the old booking until a full refresh.
  if (diaryContext && bookingResponse?.id && !bookingResponse.unsaved) {
    diaryContext.queryClient
      .invalidateQueries({
        queryKey: ["getSelectedBooking", bookingResponse.id]
      })
      .catch(() => undefined);
    diaryContext.queryClient
      .invalidateQueries({
        queryKey: ["getBookingForSelect", bookingResponse.id]
      })
      .catch(() => undefined);
  }

  if (bookingResponse?.unsaved) {
    updateBookingCacheStorage({
      booking: bookingResponse,
      venueId: diaryContext.loadedVenueId ?? undefined
    });
  }

  return bookingResponse;
};

export const addOrUpdateBooking = async ({
  booking,
  diaryContext
}: updateBookingType): Promise<ViewBooking & editBooking> => {
  const bookingId = booking.id;

  // New bookings (no id yet) can't race an existing record — write directly.
  if (!bookingId) {
    const sentLastUpdated = new Date();
    const toSend = { ...booking, lastUpdated: sentLastUpdated };
    const response = await persistBooking(toSend, diaryContext);

    // A create assigns an id and returns a fresh server last_updated. Cache it
    // against the new id so a rapid follow-up write (e.g. marking the just-
    // created walk-in "Left", which with alphaBookingActions also fires the
    // early-checkout finish-prompt duration write) rebases onto the server's
    // exact string instead of a fresh Date the API would reject as out of date.
    // The echo-back case (offline/unsaved create, where saveBooking just returns
    // what we sent) is already excluded by !response.unsaved — and an unsaved
    // create has no id to key on anyway, so newId also guards it.
    //
    // Not when areas were assigned: addBooking assigns them in a second write
    // after the create, which moves last_updated on, so the create response's
    // version is already superseded and a rapid follow-up would be rejected.
    const newId = response?.id;
    if (
      newId &&
      !response.unsaved &&
      response.lastUpdated &&
      !booking.assignedAreas?.length
    ) {
      recentServerVersion.set(newId, {
        lastUpdated: response.lastUpdated,
        at: Date.now()
      });
    }

    return response;
  }

  const previous = bookingWriteChains.get(bookingId);
  // Marked before the run IIFE is created so an echo landing in the gap
  // between this call and the write actually starting still sees the write
  // as in flight.
  markBookingWriteInFlight(bookingId);
  const run = (async () => {
    if (previous) {
      // Wait for the in-flight write to this booking to land; ignore its failure
      // (that write surfaces its own error) — we only need it to have settled so
      // recentServerVersion below reflects the current stored version.
      try {
        await previous;
      } catch {
        // ignore
      }
    }

    const recent = recentServerVersion.get(bookingId);
    const lastUpdated =
      recent?.lastUpdated && Date.now() - recent.at < RECENT_VERSION_TTL_MS
        ? recent.lastUpdated
        : new Date();
    const toSend = { ...booking, lastUpdated };
    const sentLastUpdated = toSend.lastUpdated;

    if (diaryContext) {
      const key = getBookingsListKey(
        diaryContext.date,
        diaryContext.loadedVenueId ?? undefined
      );
      updateBookingsList(diaryContext.queryClient, key, (current) => {
        if (!current) return current;
        let found = false;
        const updated = current.map((item) => {
          if (item.id !== bookingId) return item;
          found = true;
          return { ...item, lastUpdated };
        });
        return found ? updated : current;
      });

      diaryContext.dispatch(
        selectedBookingMerged({ id: bookingId, lastUpdated })
      );
    }

    const response = await persistBooking(toSend, diaryContext);

    // Only cache a version the SERVER actually returned. saveBooking merges the
    // request booking into its result, so response.lastUpdated is truthy even
    // when the server omitted last_updated (it is just the value we sent). If it
    // equals what we sent, the server gave us no fresh version — clear the cache
    // so the next write falls back to new Date() rather than rebasing onto a now
    // stale value and getting rejected (the very failure this map prevents).
    const serverVersion =
      response &&
      !response.unsaved &&
      response.lastUpdated &&
      response.lastUpdated !== sentLastUpdated
        ? response.lastUpdated
        : undefined;

    if (serverVersion) {
      recentServerVersion.set(bookingId, {
        lastUpdated: serverVersion,
        at: Date.now()
      });
    } else {
      recentServerVersion.delete(bookingId);
    }

    return response;
  })();

  bookingWriteChains.set(bookingId, run);
  run
    .catch(() => undefined)
    .finally(() => {
      clearBookingWriteInFlight(bookingId);
      if (bookingWriteChains.get(bookingId) === run) {
        bookingWriteChains.delete(bookingId);
      }
    });

  return run;
};

export const saveBooking = async (
  booking: ViewBooking & editBooking
): Promise<ViewBooking & editBooking> => {
  let unsaved = true;
  const newBooking = !booking.id;
  let updatedBookingResponse: (ViewBooking & editBooking) | undefined =
    undefined;

  if (newBooking) {
    // update array directly, resolves excessive re-rendering and changes flickering
    updatedBookingResponse = await addBooking(booking);
  }

  if (!newBooking) {
    updatedBookingResponse = await postUpdateBooking(booking);
  }

  if (updatedBookingResponse?.id) {
    unsaved = false;
  }

  return {
    ...booking,
    ...getDefinedProps(updatedBookingResponse ?? booking),
    unsaved
  };
};

export const addBooking = async (
  booking: ViewBooking
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings`;
  let response;

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(convertAllDatesToUTC(mapBookingToV4(booking)))
    });
  } catch (error) {
    errorHandler(error, "Error adding booking");
    return undefined;
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, "Error adding booking");
    return undefined;
  }

  const newBooking = await response.json();

  const viewBooking: ViewBooking = mapBookingV4ToState(
    newBooking.payload.booking
  );
  viewBooking.id = newBooking.payload.booking._id;

  const areas: Array<string> = [];
  if (booking.assignedAreas) {
    booking.assignedAreas.forEach((ViewBookingAssignedAreasInner) => {
      const areaId = `${ViewBookingAssignedAreasInner.id}`;
      areas.push(areaId);
    });
  }

  // add booking doesn't set the booking area, so grab the new id and call assignBookingArea
  if (
    viewBooking.id &&
    booking.assignedAreas &&
    booking.assignedAreas.length > 0
  ) {
    const newBookingAssignedAreas = await assignBookingArea({
      bookingId: viewBooking.id,
      appendAssignedAreasRequest: { areas }
    });

    if (newBookingAssignedAreas?.assignedAreas) {
      viewBooking.assignedAreas =
        newBookingAssignedAreas.assignedAreas as ViewBookingAssignedAreasInner[];
    }
  }
  return viewBooking;
};

const postUpdateBooking = async (
  booking: ViewBooking
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${booking.id}`;
  let response;

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(mapBookingToV4(booking))
    });
  } catch (error) {
    errorHandler(error, "Error updating booking");
    return undefined;
  }

  if (!response?.ok) {
    const errorMessage = await readErrorMessage(response);
    if (isStaleBookingConflict(errorMessage)) {
      markStaleBookingConflict(booking.id ?? "");
      notification(STALE_BOOKING_NOTIFICATION);
    } else {
      apiFetchNotOkHandler(response, "Error updating booking");
    }
    return undefined;
  }

  const newBooking = await response.json();

  const viewBooking: ViewBooking = mapBookingV4ToState(
    newBooking.payload.booking
  );

  return viewBooking;
};

export const postUpdateBookingV4 = async (
  booking: BookingV4
): Promise<BookingV4 | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${booking._id}`;
  let response;

  if (booking.preorders) {
    booking.preorders.forEach((p) =>
      assertPreorderTypes(p, "postUpdateBookingV4")
    );
  }

  try {
    response = await fetch(`${bookingUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(booking)
    });
  } catch {
    return undefined;
  }

  if (!response?.ok) {
    return undefined;
  }

  // The write moved the server's last_updated on; the cached one is now stale.
  forgetServerBookingVersion(booking._id);

  const newBooking = await response.json();

  return newBooking.payload.booking;
};

/**
 * Append the area to the booking
 * @returns
 */
const assignBookingArea = async (
  params: AppendAssignedAreasOperationRequest
) => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const config = new Configuration({
    accessToken: token,
    basePath: environmentVars?.COLLINS_API_BASE_URL
  });
  try {
    return await new BookingsApi(config).appendAssignedAreas(params);
  } catch {
    return undefined;
  }
};

export const replaceAssignedAreas = async (
  params: ReplaceAssignedAreasRequest
) => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const config = new Configuration({
    accessToken: token,
    basePath: environmentVars?.COLLINS_API_BASE_URL
  });
  try {
    return await new BookingsApi(config).replaceAssignedAreas(params);
  } catch {
    return undefined;
  }
};

/**
 * Delete a booking (soft-delete: sets status to 'deleted')
 * Uses the Collins API (collinsbookings.com), matching collins-ui BookingsProvider.delete.
 * @param bookingId - The ID of the booking to delete
 * @param reason - Optional reason for deletion: 'spam', 'duplicate', or 'other'
 * @returns true if the deletion was successful, false otherwise
 */
export const deleteBooking = async (
  bookingId: string,
  reason: string = "other"
): Promise<boolean> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${bookingId}`;

  try {
    const response = await fetch(
      `${bookingUrl}?reason=${encodeURIComponent(reason)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Build-Version": getBuildVersion()
        }
      }
    );

    if (!response?.ok) {
      await apiFetchNotOkHandler(response, "Error deleting booking");
      return false;
    }

    return true;
  } catch (error) {
    errorHandler(error, "Error deleting booking");
    return false;
  }
};

/**
 * Restore a previously deleted booking
 * Uses the Collins API (collinsbookings.com), matching collins-ui BookingsProvider.restore.
 * @param bookingId - The ID of the booking to restore
 * @returns The restored booking, or undefined on failure
 */
export const restoreBooking = async (
  bookingId: string
): Promise<ViewBooking | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${bookingId}/restore`;

  try {
    const response = await fetch(bookingUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Build-Version": getBuildVersion()
      }
    });

    if (!response?.ok) {
      await apiFetchNotOkHandler(response, "Error restoring booking");
      return undefined;
    }

    const data = await response.json();
    const booking = data.data;
    if (!booking?._id) {
      errorHandler(
        new Error("Unexpected restore response shape"),
        "Error restoring booking"
      );
      return undefined;
    }
    return mapBookingV4ToState(booking);
  } catch (error) {
    errorHandler(error, "Error restoring booking");
    return undefined;
  }
};

export const mapBookingToV4 = (booking: ViewBooking): BookingV4 => {
  const bookingV4 = convertObjectCamelCaseToSnakeCase(
    booking
  ) as unknown as BookingV4;

  return {
    ...bookingV4,
    _id: booking.id ?? bookingV4._id,
    // The customer link, written back under the name the API actually uses.
    //
    // mapBookingV4ToState READS `user_id` into `customerId`, but the generic
    // camel-to-snake pass above turns that back into `customer_id`, which the
    // API ignores — so the link could be read and never written. Verified
    // against the API 2026-09-09: POST /bookings/{id} with `user_id` persists.
    //
    // `null` rather than undefined when cleared: undefined is dropped from the
    // payload, so detaching a customer would silently do nothing (AB#2664937).
    user_id: booking.customerId ?? null,
    // Strip preorders to prevent the Core API from re-processing them with
    // incorrectly typed data (bookingsPreorder strings vs Preorder numbers).
    // Pre-order mutations are handled separately via postUpdateBookingV4.
    preorders: undefined,
    assigned_area: bookingV4.assigned_areas?.[0]
      ? {
          id: bookingV4.assigned_areas?.[0]?.id?.toString() ?? "",
          name: bookingV4.assigned_areas?.[0]?.name?.toString() ?? ""
        }
      : null,
    assignMode: false,
    managed_by_venue: true,
    source: "admin",
    comments:
      booking.comments?.map((comment) =>
        ViewBookingCommentsInnerToJSON(comment)
      ) ?? [],
    waitlist_details: {
      ...bookingV4.waitlist_details,
      availableTime: bookingV4.waitlist_details?.available_time
    },
    booking_requirements:
      booking.bookingRequirements ?? bookingV4.booking_requirements
  } as unknown as BookingV4;
};

export const mapBookingV4ToState = (booking: Partial<BookingV4>): ViewBooking =>
  ({
    id: booking._id,
    actualGuests: booking.actual_guests ?? booking.num_people,
    actualSpend: booking.actual_spend,
    reference: booking.booking_id,
    customerId: booking.user_id,
    currentStage: booking.current_stage ?? "",
    waitlistDetails: {
      ...booking.waitlist_details,
      availableTime: booking.waitlist_details?.available_time
    },
    ...convertObjectSnakeCaseToCamelCase(booking),
    preorders: booking.preorders?.map(v4PreorderToView),
    tags: booking.tags,
    bookingRequirements: booking.booking_requirements
  }) as ViewBooking;
